import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";
import CircledBackButton from "../../../Common/components/CircledBackButton";
import { membershipManagementPageStyling } from "../../../../theme";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import {
  findMembershipByMemberIdentifier,
  MembershipDependent,
} from "@/services/core-api-adapter";
import { trackEvent } from "@/services/analytics-adapter";
import { useGlobalStore } from "@/store";
import Panel from "@/features/Common/components/Panel";
import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import useGetMemberships from "@/features/Common/hooks/useGetMemberships";
import MembershipDetailPanelV2 from "@/features/Membership/components/MembershipDetailPanelV2";
import MinorDependentsList from "../MinorDependentsList";
import IconLoader from "@/features/Common/components/IconLoader";

interface MyProfileAndMembershipProps {
  dependentsList: MembershipDependent[];
  remainingDependentsCount: number;
  maxDependentAge: number;
  onAddChildDependentButtonClick: () => void;
  onEditChildDependentButtonClick: (dependent: MembershipDependent) => void;
  onDeleteChildDependentButtonClick: (dependent: MembershipDependent) => void;
  isError: boolean;
  membershipIdentifier: string;
}

export default function MyProfileAndMembership({
  dependentsList,
  remainingDependentsCount,
  maxDependentAge,
  onAddChildDependentButtonClick,
  onEditChildDependentButtonClick,
  onDeleteChildDependentButtonClick,
  isError = false,
  membershipIdentifier,
}: MyProfileAndMembershipProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { memberships, membershipsFetchError, isMembershipsLoading } =
    useGetMemberships();

  const MIN_ALLOWED_DEPENDENTS = 1;

  function onAddDependentButtonHandler() {
    trackEvent({
      event: "action.addDependent",
      source: "My profile",
    });
    onAddChildDependentButtonClick();
  }

  function onEditDependentButtonHandler(dependent: MembershipDependent) {
    trackEvent({
      event: "action.editDependent",
      source: "My profile",
    });
    onEditChildDependentButtonClick(dependent);
  }

  function onDeleteDependentButtonHandler(dependent: MembershipDependent) {
    trackEvent({
      event: "action.deleteDependent",
      source: "My profile",
    });
    onDeleteChildDependentButtonClick(dependent);
  }

  return (
    <Stack spacing={2} sx={{ ...membershipManagementPageStyling }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <CircledBackButton showLabel={true} onClick={() => navigate(-1)} />
      </Stack>

      <Typography component="h1" variant="h2">
        {isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE)
          ? t("MembershipDetailPageV2.title")
          : t("MembershipDetailPage.title")}
      </Typography>

      <MembershipDetailPanelV2
        membershipIdentifier={membershipIdentifier}
        membership={
          findMembershipByMemberIdentifier(membershipIdentifier, memberships) ||
          undefined
        }
        membershipsFetchError={membershipsFetchError}
        isMembershipsLoading={isMembershipsLoading}
      />

      {!isError &&
        isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE) && (
          <Panel>
            <Stack spacing={2}>
              <Stack mb={2}>
                <Typography variant="h3" color="GrayText">
                  {t("MyProfile.minorDependents.title")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("MyProfile.minorDependents.description")}
                </Typography>
              </Stack>
              {Array.isArray(dependentsList) && dependentsList && (
                <MinorDependentsList
                  dependentsList={dependentsList}
                  onEditButtonClick={onEditDependentButtonHandler}
                  onDeleteButtonClick={onDeleteDependentButtonHandler}
                  isError={isError}
                />
              )}
              <ButtonWithAnalytics
                page="My profile"
                text={t("MyProfile.dependants.addDependant.CTA")}
                intent="informational"
                variant="contained"
                size="small"
                fullWidth
                disabled={remainingDependentsCount < MIN_ALLOWED_DEPENDENTS}
                onClick={onAddDependentButtonHandler}
                startIcon={<IconLoader icon="PlusIcon" />}
              >
                {t("MyProfile.minorDependants.addDependant.CTA")}
              </ButtonWithAnalytics>

              <Typography
                color="neutral.500"
                textAlign="center"
                variant="body2"
              >
                {t("MyProfile.dependants.maxDependants", {
                  maxDependentAge: maxDependentAge,
                })}
              </Typography>
            </Stack>
          </Panel>
        )}
    </Stack>
  );
}
